import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import CampaignsModalMenuItem from 'components/Campaigns/Details/ModalMenuItem';
import CampaignsModalItemSelection from 'components/Campaigns/Details/ModalItemSelection';
import ViewCampaignBaseSelection, {
  ChildrenArgsType,
  ChildrenReturnType,
} from 'containers/Campaigns/View/BaseSelection';
import CampaignsDetailsDataSelection from 'components/Campaigns/Details/DataSelection';
import QueryRetryWrapper from 'components/QueryRetryWrapper';
import { GET_PRODUCTS_BY_ID } from 'apollo/queries';
import { productsClient } from 'apollo';
import { getUpcFromItem } from 'utils/functions';
import * as createEditActions from 'containers/Campaigns/CreateEdit/store/actions';
import * as actions from 'containers/Campaigns/actions';
import { CampaignUpdateActionContext, CampaignUpdateActionContextType } from 'utils/campaigns';
import { CAMPAIGNS_TYPE, campaignsDataType, StoreDataType } from 'utils/types';
import * as selectors from 'containers/Campaigns/selectors';
import { currentBrandSelector, isEnterpriseContextSelector } from 'store/global/selectors';
import LoadingSpinner from 'components/LoadingSpinner';
import { queryStores } from 'utils/queries';

const MENU_ITEMS = Object.freeze({
  ACCOUNTS: 'accounts',
  ITEMS: 'items',
  STORES: 'stores',
  DETAILS: 'details',
});

interface Props {
  clientId: string;
  data: campaignsDataType;
}

const ViewCampaignDetails: React.FC<Props> = ({ clientId, data }) => {
  const dispatch = useDispatch();
  const currentTab: CampaignUpdateActionContextType = useSelector(selectors.currentTabSelector);
  const currentBrand: string = useSelector(currentBrandSelector);
  const isEnterpriseContext: boolean = useSelector(isEnterpriseContextSelector);
  const [stores, setStores] = useState(null);

  const handleEdit = (id) => {
    dispatch(
      createEditActions.openEditCampaignModal(
        id,
        currentTab === CAMPAIGNS_TYPE
          ? CampaignUpdateActionContext.CAMPAIGNS
          : CampaignUpdateActionContext.ARCHIVED
      )
    );
    dispatch(actions.setShow(false));
  };

  const renderLeftMenu = ({ currentMenu, onMenuChange }) => {
    return (
      <React.Fragment>
        <CampaignsModalMenuItem
          label="Details"
          menuId={MENU_ITEMS.DETAILS}
          selectedMenuId={currentMenu}
          onSelect={onMenuChange}
        />
        {data.itemFilters.items.length > 0 && (
          <CampaignsModalMenuItem
            label="Items"
            menuId={MENU_ITEMS.ITEMS}
            selectedMenuId={currentMenu}
            onSelect={onMenuChange}
          />
        )}
        {data.storeFilters.stores.length > 0 && (
          <CampaignsModalMenuItem
            label="Stores"
            menuId={MENU_ITEMS.STORES}
            selectedMenuId={currentMenu}
            onSelect={onMenuChange}
          />
        )}
        {data.storeFilters.accounts.length > 0 && (
          <CampaignsModalMenuItem
            label="Accounts"
            menuId={MENU_ITEMS.ACCOUNTS}
            selectedMenuId={currentMenu}
            onSelect={onMenuChange}
          />
        )}
        <Button className="btn-edit-campaign" onClick={() => handleEdit(data.id)}>
          EDIT
        </Button>
      </React.Fragment>
    );
  };

  const getInfoForItem = (item: any, backendItems: Array<any>) => {
    const backendItem = backendItems.find((backendItem) => backendItem.id === item.slice(2));
    if (backendItem) {
      return { firstLabel: backendItem.description, secondLabel: getUpcFromItem(backendItem) };
    }
    return { firstLabel: item };
  };

  const createItemFromStore = (store: StoreDataType) => {
    const firstLabel = `${store.name !== null ? store.name : store.address} - ${store.city}`;
    const secondLabel = `${store.address}, ${store.city}, ${store.state} ${store.postalCode}`;
    return { firstLabel, secondLabel };
  };

  const createStoreKeyFromData = (store) => `${store.nielsenTag}-${store.spinsTag}`;

  const handleQueryStores = () => {
    return queryStores(clientId, data.storeFilters.stores, (storesData) => {
      setStores((previousState) => {
        // Do not add stores already present
        const previousStores = previousState && previousState.stores ? previousState.stores : [];
        let newStores = [...previousStores];
        storesData.forEach((newStore) => {
          if (
            !previousStores.some(
              (store) => createStoreKeyFromData(store) === createStoreKeyFromData(newStore)
            )
          ) {
            newStores = newStores.concat([newStore]);
          }
        });
        return newStores;
      });
    });
  };

  const renderItemSelection = ({ currentMenu }) => {
    switch (currentMenu) {
      case MENU_ITEMS.DETAILS:
        return (
          <div className="ccbs-selection-container">
            <CampaignsDetailsDataSelection data={data} />
          </div>
        );
      case MENU_ITEMS.ITEMS:
        return (
          <div className="ccbs-selection-container">
            <QueryRetryWrapper
              query={GET_PRODUCTS_BY_ID}
              client={productsClient as any}
              variables={{
                clientId,
                // The service expects the id, which is the UPC without starting '00'
                ids: data.itemFilters.items.map((item) => item.slice(2)),
                ...(isEnterpriseContext && { brand: currentBrand }),
              }}
              fetchPolicy="cache-first"
            >
              {({ data: { batchListProducts } }) => (
                <CampaignsModalItemSelection
                  items={data.itemFilters.items.map((item) =>
                    getInfoForItem(item, batchListProducts.items)
                  )}
                />
              )}
            </QueryRetryWrapper>
          </div>
        );
      case MENU_ITEMS.STORES:
        return (
          <div className="ccbs-selection-container">
            {handleQueryStores()}
            {stores === null ? (
              <LoadingSpinner />
            ) : (
              <CampaignsModalItemSelection
                items={stores.map((store) => createItemFromStore(store))}
              />
            )}
          </div>
        );
      case MENU_ITEMS.ACCOUNTS:
        return (
          <div className="ccbs-selection-container">
            <CampaignsModalItemSelection
              items={data.storeFilters.accounts.map((account) => ({ firstLabel: account }))}
            />
          </div>
        );
      default:
        return null;
    }
  };
  return (
    <ViewCampaignBaseSelection defaultMenu={MENU_ITEMS.DETAILS}>
      {(args: ChildrenArgsType): ChildrenReturnType => {
        return {
          menu: renderLeftMenu(args),
          content: renderItemSelection(args),
        };
      }}
    </ViewCampaignBaseSelection>
  );
};

export default ViewCampaignDetails;
